// ─────────────────────────────────────────────────────────────────────────────
// Eval Survival guide page
//
// Covers the prop firm simulation feature. Important framing:
//   - it's a backtest of YOUR trading history against each firm's rules
//   - the pass rate is the fraction of simulated runs that hit the target
//   - it doesn't predict future performance, it surfaces fit
//   - rules change; users should always verify against the firm's site
// ─────────────────────────────────────────────────────────────────────────────

import {
  GuideLayout, H2First, H2, P, UL, LI, UI,
  ScreenshotFrame, Callout,
} from "../GuideLayout";

export function GuideEvalSurvival() {
  return (
    <GuideLayout
      title="Eval Survival"
      subtitle="Replay your trading history against each prop firm's actual rules. See where you'd thrive — and where you'd blow the account."
      prev={{ slug: "analytics",  label: "Analytics" }}
      next={{ slug: "portfolio",  label: "Portfolio & Trading Rules" }}
    >
      <H2First>What it's for</H2First>
      <P>
        Prop firm evaluations all have the same shape — hit a profit
        target without breaking a daily drawdown, a max loss, or a
        consistency rule — but the actual numbers differ a lot. A $50K
        TopStep combine has different math than a $50K Apex eval, and
        both are different from MyFundedFutures or Lucid.
      </P>
      <P>
        Eval Survival takes your actual trading history and replays it
        against each firm's listed ruleset. The output is a pass rate:
        if you'd started a fresh eval from each day in your history,
        what fraction would you have passed? It's a fit-check against
        published rules, not a prediction.
      </P>
      <P>
        Most useful before paying for an eval — answers the question
        "based on how I've actually been trading, which firm's rules
        fit my style?" Cheaper than finding out by failing.
      </P>

      <ScreenshotFrame
        src="/guide/eval-survival-leaderboard.png"
        alt="Eval Survival leaderboard ranked by pass rate"
        caption="Eight prop firms ranked by your simulated pass rate against each one's ruleset."
      />

      <H2>Picking what to simulate</H2>
      <P>
        Two selectors at the top control the simulation:
      </P>
      <UL>
        <LI><UI>Account</UI> — which of your trading accounts to use as the source of trade history. Each account in your Portfolio is replayed separately.</LI>
        <LI><UI>Size filter</UI> — All Sizes, $25K, $50K, $100K, or $150K+. Narrows the leaderboard to firm accounts of that size.</LI>
      </UL>
      <P>
        The selected account's full trade history feeds the simulation.
        More trades = more starting points to test from = more reliable
        pass rate.
      </P>

      <H2>The leaderboard</H2>
      <P>
        Below the selectors is a leaderboard of prop firm accounts at
        the chosen size, sorted by pass rate. Each row shows:
      </P>
      <UL>
        <LI>The firm and account variant (e.g. <UI>TopStep — $50K Combine</UI>)</LI>
        <LI>The ruleset summary: target, daily drawdown, daily loss limit (if any), and whether the DLL is hard or soft</LI>
        <LI>A progress bar showing how runs ended (passed vs incomplete)</LI>
        <LI>The pass rate as a percentage</LI>
        <LI>The total number of runs simulated</LI>
      </UL>
      <P>
        Click any row to expand it for more detail about that firm's
        ruleset and how your runs ended.
      </P>

      <H2>What "pass rate" means</H2>
      <P>
        The pass rate is the fraction of simulated runs that hit the
        profit target without breaking any of the firm's rules along
        the way.
      </P>
      <P>
        A simulated "run" starts on a given day in your history and
        walks forward through your subsequent trades, applying the
        firm's rules in real-time:
      </P>
      <UL>
        <LI>If your cumulative P&L hits the profit target → run passes</LI>
        <LI>If a single day's loss exceeds the daily drawdown → run fails (blown account)</LI>
        <LI>If your total loss exceeds the max loss limit → run fails</LI>
        <LI>If you run out of trades before hitting the target → run is incomplete</LI>
      </UL>
      <P>
        Every day in your history is a potential starting point, so an
        account with 90 trading days yields ~90 simulated runs per firm.
        The pass rate is passed-runs divided by total-runs.
      </P>

      <Callout kind="tip">
        Pass rate of 0% with all runs marked "incomplete" usually means
        your history is too short to hit the firm's profit target —
        you'd need more trading days, not necessarily better trading.
        A pass rate of 0% with runs marked "blew DD" means the rules
        don't fit your style, and trading more isn't going to help.
      </Callout>

      <H2>What Holds You Back</H2>
      <P>
        Below the leaderboard is a breakdown across all simulated runs
        of <em>how</em> they ended. Categories include:
      </P>
      <UL>
        <LI><UI>Incomplete (didn't hit target)</UI> — ran out of trade history before reaching the profit target</LI>
        <LI><UI>Blew daily drawdown</UI> — a single day's loss exceeded the firm's daily DD limit</LI>
        <LI><UI>Hit max loss</UI> — cumulative loss exceeded the firm's overall max loss</LI>
        <LI><UI>Other rule violations</UI> — consistency rules, minimum trading day requirements, etc.</LI>
      </UL>
      <P>
        Read this section to understand <em>why</em> a firm's pass rate
        is low for you. If most runs fail on daily drawdown, the firm's
        DD is too tight for your sizing. If most are incomplete, you
        need more samples — keep trading and check back. If most hit
        max loss, the firm's overall risk budget doesn't match your
        drawdown patterns.
      </P>

      <H2>Expanding a firm's row</H2>
      <P>
        Click any leaderboard row to expand it for the per-run detail:
        which specific runs passed, which failed, where each one
        ended. Useful when you want to understand a borderline case —
        a firm showing 35% pass rate isn't a fail or a win, and the
        expansion shows you what the differentiator was.
      </P>

      <H2>Limits worth knowing</H2>
      <P>
        The page footer states two important caveats explicitly, but
        they're worth repeating:
      </P>
      <UL>
        <LI><strong>Prop firms change rules often.</strong> The rulesets in Eval Survival are maintained on best effort but may lag behind a firm's most recent changes. Always verify against the firm's official site before trading live.</LI>
        <LI><strong>The simulation doesn't model everything.</strong> Unusual events like overnight holds, news restrictions, micro-scalping bans, or symbol restrictions aren't accounted for. A firm that simulates well here might still reject your style for reasons outside the simulation.</LI>
      </UL>
      <P>
        Treat the leaderboard as a strong filter, not a definitive
        answer. Use it to narrow your choices from "every firm" down
        to "the 2-3 that might actually fit my style," then read those
        firms' rules carefully before signing up.
      </P>

      <Callout kind="info">
        Eval Survival is one of the more unusual features in the
        product — most trade journals don't simulate prop firm rules
        at all. The reason it works in Futures OS is that the same
        trade history you log for Analytics is the input to the
        simulation. The more accurately you log, the more reliable the
        eval results.
      </Callout>

      <H2>How to use it</H2>
      <P>
        Three realistic use cases:
      </P>
      <UL>
        <LI><strong>Before signing up for an eval.</strong> Filter to the size you're considering, look at the top 3 firms' pass rates, and pick from those. Cheaper than $200 in failed eval fees.</LI>
        <LI><strong>After a streak of losses.</strong> Re-run the simulation. If pass rates dropped sharply, you may have shifted into a style the firm's rules don't accommodate — useful data before trying again.</LI>
        <LI><strong>When choosing between two funded account sizes.</strong> Compare your pass rate on a $50K vs $100K Combine of the same firm. Larger size = more drawdown room, but also more capital to manage. The simulation tells you which fits your patterns.</LI>
      </UL>
    </GuideLayout>
  );
}
